<template>
  <div class="vx-container vx-mx-auto vx-px-3 vx-pb-32 md:vx-px-8 2xl:vx-max-w-screen-xl" >
      <page-header>Contacts</page-header>

      <contact-form v-if="showContactForm" :contact="editContactObj"></contact-form>

      <contact-view v-if="displayContact" :contact="displayContact" :segments="displayContactSegments" />

      <div v-if="shouldShowIndex()">
          <resource-management
              :table-title="tableTitle"
              :table-columns="tableColumns"
              :has-search-feature="true"
              :search-help-popover-text="`
                  <p>
                    This search function will find contacts by full or partial
                    match on first name, last name, email, or phone.
                  </p>
              `"
              :search-include-params="['tags']"
              :entity-name="entityName"
              :entity-fetch-client="entityFetchClient"
              :entity-search-client="entitySearchClient"
              @collection-changed="collectionChanged"
          >
              <template #filters="{ applyFilters }">
                  <contacts-filter @apply:filters="applyFilters"> </contacts-filter>
              </template>

              <template #actions="{ isCollectionLoading }">
                  <vx-button
                      class="resource-management__button--create"
                      appearance="primary-dark"
                      size="large"
                      left="plus"
                      :icon-size="12"
                      :has-shadow="true"
                      :disabled="isCollectionLoading"
                      @click="createContact"
                      >New Contact</vx-button
                  >
              </template>

              <template #resource-table-row="{ entity: contact }">
                  <contacts-table-row :contact="contact"></contacts-table-row>
              </template>
          </resource-management>
      </div>
  </div>
</template>

<script>
import ContactsTableRow from './contacts-list/ContactsTableRow.vue';
import ContactView from './contact-view/ContactView.vue';
import ContactsFilter from './ContactsFilter.vue';
import { VxButton } from '@voxie/frontend-components-v3';

import { contactsClient } from '../../services';
import { camelCase } from '~/utils/string';

// @todo work in progress to reskin these components
import ContactForm from './form/ContactForm.vue';
import ResourceManagement from '~/components/resource-management/ResourceManagement.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import clientRoutes from './../../mixins/client-routes';
import scrollTo from './../../mixins/scroll-to';

export default {
  mixins: [clientRoutes, scrollTo],
  inject: ['FEATURES'],

  components: {
  ContactsTableRow,
  ContactView,
  VxButton,
  ResourceManagement,
  ContactsFilter,
  ContactForm,
  PageHeader
},

  data() {
      return {
          teamId: Spark.state.currentTeam.id,
          contacts: [],
          displayContact: null,
          displayContactSegments: [],
          showContactForm: false,
          editContactObj: null,

          entityName: 'Contact',
          tableTitle: 'Contacts',
          tableColumns: [
              {
                  id: 'contact',
                  name: 'Contact',
                  classNames: [],
              },
              {
                  id: 'identity',
                  name: 'Identity',
                  classNames: [],
              },
              {
                  id: 'segments',
                  name: 'Segments',
                  classNames: ['hidden table-cell-xl'],
              },
              {
                  id: 'tags',
                  name: 'Tags',
                  classNames: ['hidden table-cell-xl'],
              },
              {
                  id: 'status',
                  name: 'Status',
                  classNames: [],
              },
              {
                  id: 'actions',
                  name: '',
                  classNames: [],
              },
          ],

          disableExport: false,
      };
  },

  mounted() {
      setTimeout(() => this.usePushStateForClientRoutes(), 500);

      this.loadCollectionAndUi();
  },

  created() {
      Bus.$emit('ContactsNav-showBtnNewContact', true);
      Bus.$emit('ContactsNav-showBtnPlaceholder', false);

      Bus.$on('contactNew', () => {
          this.createContact();
      });

      Bus.$on('ContactFormClose', () => {
          this.contactFormClose();
      });

      Bus.$on('contactShow', (id) => {
          this.showContact(id);
      });

      Bus.$on('contactClose', () => {
          this.showContactClose();
      });

      Bus.$on('contactEdit', (id) => {
          this.editContact(id);
      });

      Bus.$on('contactDelete', (id) => {
          this.deleteContact(id);
      });

      Bus.$on('contactUnsubscribe', (id) => {
          this.unsubscribeContact(id);
      });

      Bus.$on('contactSubscribe', (id) => {
          this.subscribeContact(id);
      });
  },

  methods: {
      entityFetchClient: (teamId, cursor) => contactsClient.contactsGetAll(teamId, cursor),
      entitySearchClient: (teamId, params) => contactsClient.searchContacts(teamId, params),

      collectionChanged(collection) {
          this.contacts = collection;
      },

      createContact() {
          this.showClientRoute('#/new');
          this.showContactForm = true;
      },

      editContact(contactId) {
          Bus.$emit(`contactPending-${contactId}`, true);

          contactsClient
              .contactGet(this.teamId, contactId)
              .then((response) => {
                  this.showContactForm = true;
                  this.editContactObj = response.data;
                  this.scrollToPageTop();
              })
              .catch((error) => {
                  console.log(error.response.data);
                  this.$toasted.global.platform_error();
              })
              .finally(() => {
                  Bus.$emit(`contactPending-${contactId}`, false);
              });
      },

      showContactClose() {
          this.displayContact = null;
          this.displayContactSegments = [];

          this.clearClientRoute();

          setTimeout(() => {
              this.loadCollectionAndUi();
          }, 500);
      },

      contactFormClose() {
          this.showContactForm = false;
          this.editContactObj = null;

          this.clearClientRoute();

          setTimeout(() => {
              this.loadCollectionAndUi();
          }, 500);
      },

      async showContact(contactId) {
          Bus.$emit(`contactPending-${contactId}`, true);

          try {
              const contactResp = await contactsClient.contactGet(this.teamId, contactId);
              const segmentsResp = await contactsClient.getContactSegments(this.teamId, contactId, { per_page: 100 });

              this.displayContact = contactResp.data;
              this.displayContactSegments = segmentsResp.data.data;
              this.scrollToPageTop();
          } catch (error) {
              console.log(error.response.data);
              this.$toasted.global.platform_error();
          } finally {
              Bus.$emit(`contactPending-${contactId}`, false);
          }
      },

      subscribeContact(contactId) {
          Bus.$emit(`contactPending-${contactId}`, true);

          contactsClient
              .contactSubscriptionOptIn(this.teamId, contactId, 'marketing')
              .finally(() => {
                  Bus.$emit(`contactPending-${contactId}`, false);
              });
      },

      unsubscribeContact(contactId) {
          Bus.$emit(`contactPending-${contactId}`, true);

          contactsClient
              .contactSubscriptionOptOut(this.teamId, contactId, 'marketing')
              .finally(() => {
                  Bus.$emit(`contactPending-${contactId}`, false);
              });
      },

      deleteContact(contactId) {
          Bus.$emit(`contactPending-${contactId}`, true);

          contactsClient
              .contactDelete(this.teamId, contactId)
              .then(() => {
                  Bus.$emit('contactDeleted', { id: contactId });
              })
              .catch((error) => {
                  console.log(error.response.data);
                  this.$toasted.global.platform_error();
              })
              .finally(() => {
                  Bus.$emit(`contactPending-${contactId}`, false);
              });
      },

      loadCollectionAndUi() {
          const route = this.currentClientRoute();
          const eventEntity = camelCase(this.entityName);

          if (route) {
              if (route.hash === 'pages') {
                  Bus.$emit(`${eventEntity}UpdateResourceTable`, {
                      searchParam: null,
                      cursor: route.parameters[0],
                      filters: null
                  });
              } else if (route.hash === 'new') {
                  Bus.$emit('contactNew');
              } else if (route.hash === 'edit') {
                  Bus.$emit('contactEdit', parseInt(route.parameters[0]));
              } else if (route.hash === 'show') {
                  Bus.$emit('contactShow', parseInt(route.parameters[0]));
              } else if (route.hash === 'search') {
                  Bus.$emit(`${eventEntity}UpdateResourceTable`, {
                      searchParam: route.parameters[0],
                      cursor: route.parameters[1],
                      filters: null
                  });
              }
          } else {
              Bus.$emit(`${eventEntity}UpdateResourceTable`, {
                  searchParam: null,
                  cursor: null,
                  filters: null
              });
          }
      },

      shouldShowIndex() {
          return !this.showContactForm && !this.displayContact;
      },
  },
};
</script>
<style lang="scss">
.resource-management__button--create,
.resource-management__button--export {
  height: 40px;
  white-space: nowrap;
}
</style>
